/**
 * basyd Dynamic Flavor Theme
 * ------------------------------------------------------------
 * Rein visuelle Erweiterung der Produktseite.
 *
 * Das Script liest ausschließlich die bereits ausgewählte
 * Geschmacksoption aus und setzt CSS-Variablen für:
 * - Haupt-Add-to-Cart-Button
 * - Sticky Add-to-Cart-Button
 * - Rahmen der gewählten Geschmackskarte
 * - dezenten Glow oberhalb der mobilen Buybox
 *
 * Es verändert NICHT:
 * - Varianten-IDs oder Variantenwahl
 * - Produktformular / Add-to-Cart-Ablauf
 * - Pfand / Upcharge Fees
 * - Preise, Cart Drawer oder Checkout
 *
 * NEUE GESCHMACKSSORTE:
 * Unten im Array BASYD_FLAVOR_THEMES einen neuen Block ergänzen.
 * Nicht konfigurierte Sorten nutzen automatisch das basyd-Grün.
 */
(function () {
  'use strict';

  if (window.__basydFlavorThemeV7Loaded) return;
  window.__basydFlavorThemeV7Loaded = true;

  /* ==========================================================
     BASYD FLAVOR COLOR CONTROL PANEL
     ----------------------------------------------------------
     aliases: alle möglichen Schreibweisen der Shopify-Sorte
     button:  Hintergrundfarbe des ATC-Buttons
     text:    Textfarbe des ATC-Buttons
     accent:  Auswahlrahmen der Geschmackskarte
     glow:    eine oder mehrere Farben für den Buybox-Lichtschein
     ========================================================== */
  const BASYD_FLAVOR_THEMES = [
    {
      id: 'citrus-yuzu-lime',
      aliases: [
        'Citrus Yuzu Lime',
        'Citrus Yuzu-Lime',
        'Zitrus Yuzu Lime',
        'Zitrus Yuzu-Limette',
        'Yuzu Lime'
      ],
      button: '#3F7F0D',
      text: '#FFFFFF',
      accent: '#78A92D',
      glow: ['#A8D84B'],
      glowOpacity: 0.58
    },
    {
      id: 'berry-pomegranate',
      aliases: [
        'Berry Pomegranate',
        'Berry-Pomegranate',
        'Beere Granatapfel',
        'Beeren Granatapfel',
        'Pomegranate Berry'
      ],
      button: '#B82458',
      text: '#FFFFFF',
      accent: '#D64B79',
      glow: ['#EA6F98'],
      glowOpacity: 0.54
    },
    {
      id: 'peach-maracuja',
      aliases: [
        'Peach Maracuja',
        'Peach-Maracuja',
        'Pfirsich Maracuja',
        'Pfirsich-Maracuja',
        'Peach Passionfruit'
      ],
      button: '#E56A10',
      text: '#FFFFFF',
      accent: '#FF8A3D',
      glow: ['#FFB06F'],
      glowOpacity: 0.54
    },
    {
      id: 'mix',
      aliases: [
        'Mix',
        'Mix Pack',
        'Mix-Pack',
        'Mix Paket',
        'Mix-Paket',
        'Mix Tray',
        'Mixed',
        'Probierpaket',
        'Mischpaket'
      ],
      /* Eigenständiges Iris-Violett: klar vom Citrus-Grün getrennt. */
      button: '#6552D9',
      text: '#FFFFFF',
      accent: '#8B79F6',
      glow: ['#78A92D', '#D64B79', '#F08A34'],
      glowOpacity: 0.60
    }
  ];

  /*
    Sicherer Fallback für neue, noch nicht konfigurierte Sorten:
    basyd-Grün statt einer erfundenen Geschmacksfarbe.
  */
  const DEFAULT_THEME = {
    id: 'default',
    button: '#07C56F',
    text: '#FFFFFF',
    accent: '#07C56F',
    glow: ['#07C56F'],
    glowOpacity: 0.34
  };

  const root = document.documentElement;
  let lastAppliedKey = '';
  let updateFrame = 0;
  let observer = null;

  function normalize(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/&/g, ' und ')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  function hexToRgb(hex) {
    const clean = String(hex || '').replace('#', '').trim();
    const normalized = clean.length === 3
      ? clean.split('').map((character) => character + character).join('')
      : clean;

    if (!/^[0-9a-fA-F]{6}$/.test(normalized)) {
      return { r: 21, g: 21, b: 21 };
    }

    return {
      r: parseInt(normalized.slice(0, 2), 16),
      g: parseInt(normalized.slice(2, 4), 16),
      b: parseInt(normalized.slice(4, 6), 16)
    };
  }

  function rgbTriplet(hex) {
    const color = hexToRgb(hex);
    return `${color.r} ${color.g} ${color.b}`;
  }

  function rgba(hex, alpha) {
    const color = hexToRgb(hex);
    return `rgb(${color.r} ${color.g} ${color.b} / ${alpha})`;
  }

  function buildGlow(colors) {
    const validColors = Array.isArray(colors) && colors.length > 0
      ? colors
      : DEFAULT_THEME.glow;

    if (validColors.length === 1) {
      return `radial-gradient(ellipse at center, ${rgba(validColors[0], 0.58)} 0%, ${rgba(validColors[0], 0.22)} 42%, ${rgba(validColors[0], 0)} 76%)`;
    }

    if (validColors.length === 2) {
      return [
        `radial-gradient(ellipse at 30% 50%, ${rgba(validColors[0], 0.50)} 0%, ${rgba(validColors[0], 0)} 68%)`,
        `radial-gradient(ellipse at 70% 50%, ${rgba(validColors[1], 0.46)} 0%, ${rgba(validColors[1], 0)} 68%)`
      ].join(', ');
    }

    const stops = validColors.map((color, index) => {
      const percentage = Math.round((index / (validColors.length - 1)) * 100);
      return `${rgba(color, 0.48)} ${percentage}%`;
    });

    return `linear-gradient(90deg, ${stops.join(', ')})`;
  }

  function findTheme(flavorName) {
    const normalizedFlavor = normalize(flavorName);

    if (!normalizedFlavor) return DEFAULT_THEME;

    const exactMatch = BASYD_FLAVOR_THEMES.find((theme) =>
      theme.aliases.some((alias) => normalize(alias) === normalizedFlavor)
    );

    if (exactMatch) return exactMatch;

    const partialMatch = BASYD_FLAVOR_THEMES.find((theme) =>
      theme.aliases.some((alias) => {
        const normalizedAlias = normalize(alias);

        if (normalizedAlias.length < 4) {
          return normalizedFlavor === normalizedAlias;
        }

        return (
          normalizedFlavor.includes(normalizedAlias) ||
          normalizedAlias.includes(normalizedFlavor)
        );
      })
    );

    return partialMatch || DEFAULT_THEME;
  }

  function getMainProductSection() {
    return document.querySelector('.shopify-section--main-product');
  }

  function getFlavorFieldset() {
    const section = getMainProductSection();

    return section
      ? section.querySelector('.variant-picker__option[data-basyd-option-role="flavor"]')
      : null;
  }

  function getFlavorNameFromInput(input) {
    if (!input) return '';

    const label = input.labels && input.labels.length > 0
      ? input.labels[0]
      : null;

    const accessibleLabel = label
      ? label.querySelector('.sr-only')
      : null;

    if (accessibleLabel && accessibleLabel.textContent.trim()) {
      return accessibleLabel.textContent.trim();
    }

    return label ? label.textContent.trim() : '';
  }

  function getSelectedFlavorName() {
    const fieldset = getFlavorFieldset();

    if (!fieldset) return '';

    const selectedInput = fieldset.querySelector('input[type="radio"]:checked');
    const inputFlavorName = getFlavorNameFromInput(selectedInput);

    if (inputFlavorName) return inputFlavorName;

    if (fieldset.dataset.basydSelectedValue) {
      return fieldset.dataset.basydSelectedValue;
    }

    const selectedValue = fieldset.querySelector('.variant-picker__option-info .h-stack > span:last-child');

    return selectedValue ? selectedValue.textContent.trim() : '';
  }


  function getVisibleOptionCount(optionElement) {
    if (!optionElement) return 0;

    const values = optionElement.querySelector('.variant-picker__option-values');
    if (!values) return 0;

    const role = optionElement.dataset.basydOptionRole || '';
    const selector = role === 'pack-size'
      ? '.block-swatch'
      : role === 'flavor'
        ? '.thumbnail-swatch'
        : '.thumbnail-swatch, .block-swatch';

    return Array.from(values.querySelectorAll(selector)).filter((element) => {
      if (!element || element.hidden || element.hasAttribute('hidden')) return false;

      const style = window.getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden') return false;

      const rect = element.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return false;

      return true;
    }).length;
  }

  function applyGridLayout() {
    const section = getMainProductSection();
    if (!section) return;

    const flavorOption = section.querySelector('.variant-picker__option[data-basyd-option-role="flavor"]');
    const packOption = section.querySelector('.variant-picker__option[data-basyd-option-role="pack-size"]');

    const flavorValues = flavorOption ? flavorOption.querySelector('.variant-picker__option-values') : null;
    const packValues = packOption ? packOption.querySelector('.variant-picker__option-values') : null;

    const detectedFlavorCount = getVisibleOptionCount(flavorOption);
    const detectedPackCount = getVisibleOptionCount(packOption);

    const flavorCount = Math.max(1, Math.min(detectedFlavorCount || 1, 4));
    const packCount = Math.max(1, Math.min(detectedPackCount || 1, 2));

    const targets = [section, document.documentElement];

    targets.forEach((target) => {
      if (!target || !target.style) return;
      target.style.setProperty('--basyd-flavor-columns-current', String(flavorCount));
      target.style.setProperty('--basyd-pack-columns-current', String(packCount));
    });

    if (flavorValues) {
      flavorValues.style.setProperty('--basyd-flavor-columns-current', String(flavorCount));
    }

    if (packValues) {
      packValues.style.setProperty('--basyd-pack-columns-current', String(packCount));
    }
  }

  function applyTheme(theme, flavorName) {
    const safeTheme = theme || DEFAULT_THEME;
    const applicationKey = `${safeTheme.id}:${normalize(flavorName)}`;

    if (applicationKey === lastAppliedKey) return;
    lastAppliedKey = applicationKey;

    root.style.setProperty('--basyd-active-flavor-button', rgbTriplet(safeTheme.button));
    root.style.setProperty('--basyd-active-flavor-text', rgbTriplet(safeTheme.text));
    root.style.setProperty('--basyd-active-flavor-accent', rgbTriplet(safeTheme.accent));
    root.style.setProperty('--basyd-active-flavor-glow', buildGlow(safeTheme.glow));
    root.style.setProperty('--basyd-active-flavor-glow-opacity', String(safeTheme.glowOpacity ?? 0.65));

    root.dataset.basydActiveFlavor = safeTheme.id;
    root.classList.add('basyd-flavor-theme-ready');
  }

function updateMixPackOptions(flavorName) {
  const section = getMainProductSection();
  if (!section) return;

  const packField = section.querySelector(
    '.variant-picker__option[data-basyd-option-role="pack-size"]'
  );
  if (!packField) return;

  const isMix = findTheme(flavorName).id === 'mix';

  const cards = Array.from(
    packField.querySelectorAll('label.block-swatch')
  );

  const packName = (card) =>
    (card.querySelector('span')?.textContent || '')
      .trim();

  cards.forEach((card) => {
    const excluded = /^(24|48)(?:\D|$)/.test(packName(card));

    if (isMix && excluded) {
      card.style.setProperty('display', 'none', 'important');
    } else {
      card.style.removeProperty('display');
    }
  });

  if (!isMix) return;

  const selected = packField.querySelector(
    'input[type="radio"]:checked'
  );

  const selectedCard = cards.find(
    (card) => card.htmlFor === selected?.id
  );

  if (
    selectedCard &&
    /^(24|48)(?:\D|$)/.test(packName(selectedCard))
  ) {
    const twelvePack = cards.find(
      (card) => /^12(?:\D|$)/.test(packName(card))
    );

    if (twelvePack && !twelvePack.classList.contains('is-disabled')) {
      twelvePack.click();
    }
  }
}


  function updateFromDom() {
    applyGridLayout();

    const flavorName = getSelectedFlavorName();

    if (!flavorName) return;

    applyTheme(findTheme(flavorName), flavorName);
    updateMixPackOptions(flavorName);
  }

  function scheduleUpdate() {
    if (updateFrame) {
      window.cancelAnimationFrame(updateFrame);
    }

    updateFrame = window.requestAnimationFrame(() => {
      updateFrame = 0;
      updateFromDom();
    });
  }

  function handleChange(event) {
    const input = event.target.closest(
      '.shopify-section--main-product .variant-picker__option[data-basyd-option-role="flavor"] input[type="radio"]'
    );

    if (!input) return;

    const immediateFlavorName = getFlavorNameFromInput(input);

    if (immediateFlavorName) {
      applyTheme(findTheme(immediateFlavorName), immediateFlavorName);
    }

    /* Stretch rendert Teile der Produktinformation neu. Danach erneut abgleichen. */
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(scheduleUpdate);
    });
  }

  function startObserver() {
    if (observer) observer.disconnect();

    observer = new MutationObserver(scheduleUpdate);
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  function boot() {
    applyGridLayout();
    updateFromDom();
    startObserver();
  }

  document.addEventListener('change', handleChange);
  document.addEventListener('variant:change', scheduleUpdate);
  document.addEventListener('product:variant-change', scheduleUpdate);
  document.addEventListener('shopify:section:load', boot);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
